import axios, { type InternalAxiosRequestConfig } from 'axios'
import { idDoDispositivo } from '../lib/dispositivo'
import { useAuthStore } from '../store/authStore'
import { useContextoStore } from '../store/contextoStore'
import { criarRenovador, type ParDeTokens } from './renovacao'

// Sem valor padrão: um build sem VITE_API_URL falha em vez de falar com a API errada em silêncio.
const BASE_URL = import.meta.env.VITE_API_URL
if (!BASE_URL) throw new Error('VITE_API_URL não definida. Copie .env.example para .env e ajuste.')

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
})

const CABECALHO_DISPOSITIVO = 'X-Walleon-Dispositivo'
const MENSAGEM_SEM_REDE = 'Não consegui renovar sua sessão. Confira a conexão e tente de novo.'

interface ApiEnvelope {
  success: boolean
  message?: string
  data?: unknown
}

// POST /api/auth/refresh por um cliente à parte: sem os interceptors abaixo, para uma resposta 401 da própria
// renovação não disparar outra renovação.
const http = axios.create({ baseURL: BASE_URL, timeout: 20_000 })

async function chamarRefresh(refreshToken: string): Promise<ParDeTokens> {
  const r = await http.post<ApiEnvelope>(
    '/api/auth/refresh',
    { refreshToken },
    { headers: { [CABECALHO_DISPOSITIVO]: idDoDispositivo() } },
  )
  const dados = r.data?.data as ParDeTokens | undefined
  if (!dados?.accessToken || !dados.refreshToken) throw new Error('Resposta de renovação inesperada.')
  return dados
}

/**
 * Mantém a sessão viva: renova o token de acesso sozinho (uma renovação por vez, também entre abas) e só desloga
 * quando o servidor diz que a sessão não vale mais. Sem rede ou com o servidor fora, a sessão continua.
 * A lógica é a mesma do Mobo (cópia de shared/src/api/renovacao.ts).
 */
export const renovador = criarRenovador({
  lerAccessToken: () => useAuthStore.getState().accessToken,
  lerRefreshToken: () => useAuthStore.getState().refreshToken,
  chamarRefresh,
  aoRenovar: (tokens) => useAuthStore.getState().atualizarTokens(tokens),
  aoEncerrar: () => useAuthStore.getState().logout({ expirada: true }),
  // Duas abas não renovam juntas: a segunda espera a vez, relê o que a primeira gravou e segue com o token novo.
  emExclusao: (tarefa) => (navigator.locks ? navigator.locks.request('walleon-renovar-sessao', tarefa) : tarefa()),
  antesDeRenovar: () => useAuthStore.persist.rehydrate(),
})

// Inject Bearer token and, when viewing a shared account, whose data to read.
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) config.headers.Authorization = `Bearer ${token}`
  config.headers[CABECALHO_DISPOSITIVO] = idDoDispositivo()

  const { selecionados, incluirPropria, principalId } = useContextoStore.getState()
  const ownId = useAuthStore.getState().userId

  // 'self' cobre sessões antigas, salvas antes de o id do usuário passar a ser guardado.
  const contas = [
    ...(incluirPropria ? [ownId ?? 'self'] : []),
    ...selecionados.map((c) => c.ownerUserId),
  ]

  // Sem header, a API usa só a conta do token.
  const somenteAPropria = contas.length <= 1 && selecionados.length === 0
  if (!somenteAPropria) config.headers['X-Walleon-Contextos'] = contas.join(',')
  if (principalId && principalId !== ownId) config.headers['X-Walleon-Contexto'] = principalId

  return config
})

function isEnvelope(body: unknown): body is ApiEnvelope {
  return typeof body === 'object' && body !== null && !(body instanceof Blob) && 'success' in body
}

api.interceptors.response.use(
  (r) => {
    if (isEnvelope(r.data)) r.data = r.data.data
    return r
  },
  async (err) => {
    const original = err.config as (InternalAxiosRequestConfig & { _jaRenovou?: boolean }) | undefined
    const ehRotaDeAuth = (original?.url ?? '').startsWith('/api/auth/')

    if (err.response?.status === 401 && original && !ehRotaDeAuth) {
      if (!original._jaRenovou && renovador.temSessao()) {
        original._jaRenovou = true
        const resultado = await renovador.renovar()
        if (resultado === 'ok') return api(original)
        // 'recusada': o renovador já encerrou a sessão. 'sem-rede': mantém a sessão e conta a verdade.
        if (resultado === 'sem-rede') err.message = MENSAGEM_SEM_REDE
        return Promise.reject(err)
      }
      // Sem refresh token guardado (sessão antiga) ou 401 mesmo depois de renovar: a sessão não serve mais.
      renovador.encerrar()
    }

    const body = err.response?.data
    if (isEnvelope(body) && body.message) err.message = body.message
    return Promise.reject(err)
  },
)
